import { EyeFilled } from '@creditornot/cb-icons';
import React, { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';

import { ConfirmModal } from 'common/components';
import { usePluralization } from 'common/hooks';
import useCurrentUser from 'common/hooks/useCurrentUser';
import { useBulkChangeEnableStatusTracking } from 'modules/menu/hooks/telemetryHooks';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import { OfferOverridesEntry, OfferWithSelectionAndAuthor, OfferingApiError } from 'modules/shared-menu/types';

import ActionButton from './ActionButton';
import BulkActionErrorList from './BulkActionErrorList';
import { BulkActionModalTitle } from './BulkActionModalTitle';
import { Action, OptionPopover, OptionPopoverContent } from './styled';
import { buildBatchEnableDisableOfferOverridesPayloads, buildBatchEnableDisableOfferPayloads } from './transformers';
import { useBatchUpdateOfferOverrides } from './useBatchUpdateOfferOverrides';
import { useBatchUpdateOffers } from './useBatchUpdateOffers';
import { updateLocalSelectionData } from './utils';
import { useVenueSelectionOverrides } from '../../../hooks/useSelectionOverrides';

type BulkEnableDisableProps = {
  selectedIds: Set<string>;
};

export const BulkEnableDisable = ({ selectedIds }: BulkEnableDisableProps) => {
  const intl = useIntl();
  const { formatPluralMessage } = usePluralization();
  const [showStatusOptions, setShowStatusOptions] = useState(false);
  const [shownModal, setShownModal] = useState<'enable' | 'disable' | null>(null);
  const { data: user } = useCurrentUser();
  const { offers, selection, updateSelectionData } = useSelectionData();
  const { executeBatchUpdateOffers, isLoading } = useBatchUpdateOffers();
  const { executeBatchUpdateOfferOverrides, isLoading: isLoadingOverridesUpdate } = useBatchUpdateOfferOverrides();
  const { updateSelectionOverridesCache } = useVenueSelectionOverrides();
  const { selectionOverrides } = useVenueSelectionOverrides();
  const { vlmeConfig } = useSelectionInfoContext();
  const isSharedVenue = !!vlmeConfig?.isSharedVenue;

  const trackChangeStatus = useBulkChangeEnableStatusTracking([...selectedIds]);

  const onSuccess = useCallback(
    (successfullyUpdatedOffers: OfferWithSelectionAndAuthor[]) => {
      toast.success(
        formatPluralMessage({
          id: 'shared-menu.bulk-actions.toasts.update.success-description',
          count: successfullyUpdatedOffers.length,
        }),
      );

      updateLocalSelectionData(successfullyUpdatedOffers, updateSelectionData);
    },
    [formatPluralMessage, updateSelectionData],
  );

  const onOverridesSuccess = useCallback(
    (successfullyUpdatedOffers: OfferOverridesEntry[]) => {
      toast.success(
        formatPluralMessage({
          id: 'shared-menu.bulk-actions.toasts.update.success-description',
          count: successfullyUpdatedOffers.length,
        }),
      );

      updateSelectionOverridesCache(successfullyUpdatedOffers);
    },
    [formatPluralMessage, updateSelectionOverridesCache],
  );

  const onError = useCallback(
    (errors: OfferingApiError[]) => {
      toast.error(
        <BulkActionErrorList
          allOffers={offers}
          errors={errors}
          selectionPrimaryLanguage={selection.primary_language}
          description={intl.formatMessage({ id: 'shared-menu.bulk-actions.toasts.update.error-description' })}
        />,
        { duration: Infinity },
      );
    },
    [intl, offers, selection.primary_language],
  );

  const handleConfirm = useCallback(
    async ({ enabled }: { enabled: boolean }) => {
      const offersToUpdate = offers.filter(offer => selectedIds.has(offer.id));

      const offerPayloads = buildBatchEnableDisableOfferPayloads({
        enabled,
        offers: offersToUpdate,
        selectionId: selection.id,
        userId: user?._id.$oid ?? '',
      });

      await executeBatchUpdateOffers({
        offerPayloads,
        onSuccess,
        onError,
      });

      trackChangeStatus(enabled);
      setShownModal(null);
    },
    [
      executeBatchUpdateOffers,
      offers,
      onError,
      onSuccess,
      selectedIds,
      selection.id,
      trackChangeStatus,
      user?._id.$oid,
    ],
  );

  const handleOverridesConfirm = useCallback(
    async ({ enabled }: { enabled: boolean }) => {
      const overridesPayloads = buildBatchEnableDisableOfferOverridesPayloads({
        enabled,
        selectedIds,
        selectionOverrides,
      });

      await executeBatchUpdateOfferOverrides({
        overridesPayloads,
        selectionId: selection.id,
        onSuccess: onOverridesSuccess,
        onError,
      });

      trackChangeStatus(enabled);
      setShownModal(null);
    },
    [
      executeBatchUpdateOfferOverrides,
      onError,
      onOverridesSuccess,
      selectedIds,
      selection.id,
      selectionOverrides,
      trackChangeStatus,
    ],
  );

  return (
    <>
      <OptionPopover
        content={
          <OptionPopoverContent>
            <Action variant="black" onClick={() => setShownModal('enable')}>
              <FormattedMessage id="menu.bulkActions.statusChange.enableItems" />
            </Action>
            <Action variant="red" onClick={() => setShownModal('disable')}>
              <FormattedMessage id="menu.bulkActions.statusChange.disableItems" />
            </Action>
          </OptionPopoverContent>
        }
        onClickInside={() => setShowStatusOptions(false)}
        onClickOutside={() => setShowStatusOptions(false)}
        show={showStatusOptions}
      >
        <ActionButton
          icon={<EyeFilled />}
          onClick={() => setShowStatusOptions(!showStatusOptions)}
          tooltipTitle={<FormattedMessage id="menu.bulkActions.statusChange.enableOrDisable" />}
        />
      </OptionPopover>
      {shownModal === 'enable' && (
        <ConfirmModal
          cancelText={<FormattedMessage id="state.cancel" />}
          confirmText={<FormattedMessage id="menu-item.enable" />}
          description={<FormattedMessage id="menu.bulkActions.statusChange.enableItemsDescription" />}
          header={
            <BulkActionModalTitle
              count={selectedIds.size}
              title={<FormattedMessage id="menu.bulkActions.statusChange.enableItems" />}
            />
          }
          loading={isSharedVenue ? isLoadingOverridesUpdate : isLoading}
          onCancel={() => setShownModal(null)}
          onConfirm={() => {
            if (isSharedVenue) {
              handleOverridesConfirm({ enabled: true });
              return;
            }

            handleConfirm({ enabled: true });
          }}
          open
        />
      )}
      {shownModal === 'disable' && (
        <ConfirmModal
          cancelText={<FormattedMessage id="state.cancel" />}
          confirmText={<FormattedMessage id="menu-item.disable" />}
          confirmVariant={'lightRed'}
          description={<FormattedMessage id="menu.bulkActions.statusChange.disableItemsDescription" />}
          header={
            <BulkActionModalTitle
              count={selectedIds.size}
              title={<FormattedMessage id="menu.bulkActions.statusChange.disableItems" />}
            />
          }
          loading={isSharedVenue ? isLoadingOverridesUpdate : isLoading}
          onCancel={() => setShownModal(null)}
          onConfirm={() => {
            if (isSharedVenue) {
              handleOverridesConfirm({ enabled: false });
              return;
            }

            handleConfirm({ enabled: false });
          }}
          open
        />
      )}
    </>
  );
};
